/**
 * Session flags for native shells (e.g. Electron).
 * Embedded: shell sets `sessionStorage[SESSION_KEY_EMBEDDED_SHELL]` before the SPA runs.
 * First-visit download: "Not now" and "View more" use sessionStorage; an installer link on /download-apps sets localStorage.
 * URLs with `?seoIndex=1` skip the install sheet (sitemap / SEO landings).
 */

export const SESSION_KEY_EMBEDDED_SHELL = "kivu_es";

/** Prior embedded marker; migrated to {@link SESSION_KEY_EMBEDDED_SHELL} on read. */
const LEGACY_SESSION_KEY_EMBEDDED_SHELL = "kivu_embedded_shell";

/** localStorage — download completed; value `"download"`. */
export const STORAGE_KEY_FIRST_VISIT_DOWNLOAD_POPUP = "kivu_ld";

/** sessionStorage — dismissed this tab; value `"1"`. */
export const SESSION_KEY_FIRST_VISIT_NOT_NOW = "kivu_sd";

/** Old monolithic first-visit key (local + session); value string must stay for migration. */
const LEGACY_FV_POPUP = "kivu_first_visit_download_popup";

function safeSessionGet(key: string): string | null {
  if (typeof window === "undefined" || typeof sessionStorage === "undefined") {
    return null;
  }
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSessionSet(key: string, value: string): void {
  if (typeof window === "undefined" || typeof sessionStorage === "undefined") {
    return;
  }
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* quota / private mode */
  }
}

function safeLocalGet(key: string): string | null {
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    return null;
  }
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeLocalSet(key: string, value: string): void {
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    return;
  }
  try {
    localStorage.setItem(key, value);
  } catch {
    /* quota / private mode */
  }
}

function safeLocalRemove(key: string): void {
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    return;
  }
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** Migrates legacy localStorage under {@link LEGACY_FV_POPUP}. */
function migrateLegacyLocalFirstVisit(): void {
  const v = safeLocalGet(LEGACY_FV_POPUP);
  if (v == null) return;
  safeLocalRemove(LEGACY_FV_POPUP);
  if (
    v === "download" &&
    safeLocalGet(STORAGE_KEY_FIRST_VISIT_DOWNLOAD_POPUP) == null
  ) {
    safeLocalSet(STORAGE_KEY_FIRST_VISIT_DOWNLOAD_POPUP, "download");
  }
}

/** Migrates legacy sessionStorage that mixed download + dismiss in one key. */
function migrateLegacyFirstVisitSession(): void {
  const sessionVal = safeSessionGet(LEGACY_FV_POPUP);
  if (sessionVal == null) return;
  try {
    sessionStorage.removeItem(LEGACY_FV_POPUP);
  } catch {
    /* ignore */
  }
  if (sessionVal === "download") {
    if (safeLocalGet(STORAGE_KEY_FIRST_VISIT_DOWNLOAD_POPUP) == null) {
      safeLocalSet(STORAGE_KEY_FIRST_VISIT_DOWNLOAD_POPUP, "download");
    }
    return;
  }
  if (sessionVal === "dismiss") {
    safeSessionSet(SESSION_KEY_FIRST_VISIT_NOT_NOW, "1");
  }
}

/** Copies legacy `kivu_embedded_shell` → `kivu_es` once per session. */
function migrateEmbeddedShellSessionKey(): void {
  if (safeSessionGet(SESSION_KEY_EMBEDDED_SHELL) === "1") {
    if (safeSessionGet(LEGACY_SESSION_KEY_EMBEDDED_SHELL) === "1") {
      try {
        sessionStorage.removeItem(LEGACY_SESSION_KEY_EMBEDDED_SHELL);
      } catch {
        /* ignore */
      }
    }
    return;
  }
  if (safeSessionGet(LEGACY_SESSION_KEY_EMBEDDED_SHELL) === "1") {
    safeSessionSet(SESSION_KEY_EMBEDDED_SHELL, "1");
    try {
      sessionStorage.removeItem(LEGACY_SESSION_KEY_EMBEDDED_SHELL);
    } catch {
      /* ignore */
    }
  }
}

/** True when running inside a shell that set embedded sessionStorage before the SPA runs. */
export function isEmbeddedShellActive(): boolean {
  migrateEmbeddedShellSessionKey();
  return safeSessionGet(SESSION_KEY_EMBEDDED_SHELL) === "1";
}

/** True when URL has `?seoIndex=1` (sitemap / crawler landing). */
export function isSeoIndexLanding(): boolean {
  if (typeof window === "undefined" || typeof window.location === "undefined") {
    return false;
  }
  try {
    return new URLSearchParams(window.location.search).get("seoIndex") === "1";
  } catch {
    return false;
  }
}

export function shouldShowFirstVisitDownloadPopup(): boolean {
  if (isSeoIndexLanding()) return false;
  if (isEmbeddedShellActive()) return false;
  migrateLegacyLocalFirstVisit();
  migrateLegacyFirstVisitSession();
  if (safeLocalGet(STORAGE_KEY_FIRST_VISIT_DOWNLOAD_POPUP) === "download") {
    return false;
  }
  if (safeSessionGet(SESSION_KEY_FIRST_VISIT_NOT_NOW) === "1") {
    return false;
  }
  return true;
}

/** User clicked a desktop installer link on /download-apps — do not show again across sessions. */
export function markFirstVisitPopupDownloadClicked(): void {
  safeLocalSet(STORAGE_KEY_FIRST_VISIT_DOWNLOAD_POPUP, "download");
}

/** User dismissed ("Not now", close, backdrop) — hide until this tab session ends. */
export function markFirstVisitPopupDismissed(): void {
  safeSessionSet(SESSION_KEY_FIRST_VISIT_NOT_NOW, "1");
}

/** sessionStorage — Teta opened (manually or auto) this tab session; value `"1"`. */
export const SESSION_KEY_TETA_AUTO_POPUP = "kivu_teta_ap";

/** True after Teta was opened once in this browser tab session. */
export function hasTetaAutoPopupOccurred(): boolean {
  return safeSessionGet(SESSION_KEY_TETA_AUTO_POPUP) === "1";
}

/** Skip Teta auto-open for this tab session (manual open or auto popup). */
export function markTetaAutoPopupOccurred(): void {
  safeSessionSet(SESSION_KEY_TETA_AUTO_POPUP, "1");
}
